import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Icon } from '@atoms/index';
import { useAuthStore } from '@store/authStore';
import { useCartStore } from '@store/cartStore';
import { useLocationStore } from '@store/locationStore';
import { useRestaurantStore } from '@store/restaurantStore';
import CartDrawer from './CartDrawer';
import AddressSelector from './AddressSelector';
import CitySelector from './CitySelector';
import SearchResultsDropdown from './SearchResultsDropdown';
import { catalogService } from '@services/catalogService';
import type { SearchRestaurantHit, SearchProductHit } from '@/types';
import { ToptanNavButton } from '@molecules/ToptanNavButton';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { items, syncFromBackend, resetToEmpty } = useCartStore();
  const { serviceZone: locationServiceZone, activeAddress } = useLocationStore();
  const { restaurant, loadRestaurant } = useRestaurantStore();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [headerSearchResults, setHeaderSearchResults] = useState<{ restaurants: SearchRestaurantHit[]; products: SearchProductHit[] }>({ restaurants: [], products: [] });
  const [isHeaderSearching, setIsHeaderSearching] = useState(false);
  const [showHeaderSearchResults, setShowHeaderSearchResults] = useState(false);
  const headerSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerSearchContainerRef = useRef<HTMLDivElement>(null);

  const isRestaurantPanel = location.pathname.startsWith('/restaurant-panel');
  const isCustomerFacingHidden = user?.role === 'ADMIN' || user?.role === 'RESTAURANT';

  const cartItemsCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const userDisplayName =
    user?.name?.trim() ||
    user?.email?.trim() ||
    user?.phone?.trim() ||
    'Hesabım';

  useEffect(() => {
    if (isRestaurantPanel || isCustomerFacingHidden) {
      resetToEmpty();
      return;
    }
    syncFromBackend().catch((err) => {
      console.error('Failed to sync cart on initial load:', err);
    });
  }, [isRestaurantPanel, isCustomerFacingHidden, syncFromBackend, resetToEmpty]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'RESTAURANT' && !restaurant) {
      loadRestaurant().catch(() => {});
    }
  }, [isAuthenticated, user?.role, restaurant, loadRestaurant]);

  const performHeaderSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setHeaderSearchResults({ restaurants: [], products: [] });
      setShowHeaderSearchResults(false);
      return;
    }

    setIsHeaderSearching(true);
    setShowHeaderSearchResults(true);

    try {
      const result = await catalogService.search(query, {
        serviceZoneId: locationServiceZone?.id || undefined,
        type: 'all',
        limit: 5,
        latitude: activeAddress?.latitude,
        longitude: activeAddress?.longitude,
      });
      setHeaderSearchResults({
        restaurants: result.restaurants?.hits || [],
        products: result.products?.hits || [],
      });
    } catch {
      setHeaderSearchResults({ restaurants: [], products: [] });
    } finally {
      setIsHeaderSearching(false);
    }
  }, [locationServiceZone, activeAddress]);

  useEffect(() => {
    if (headerSearchDebounceRef.current) clearTimeout(headerSearchDebounceRef.current);

    if (headerSearchQuery.length < 2) {
      setShowHeaderSearchResults(false);
      setHeaderSearchResults({ restaurants: [], products: [] });
      return;
    }

    headerSearchDebounceRef.current = setTimeout(() => {
      performHeaderSearch(headerSearchQuery);
    }, 300);

    return () => {
      if (headerSearchDebounceRef.current) clearTimeout(headerSearchDebounceRef.current);
    };
  }, [headerSearchQuery, performHeaderSearch]);

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-[minmax(0,auto)_1fr_minmax(0,auto)] items-center h-20 py-4 gap-4">
            {/* Premium Logo with enhanced design */}
            <Link
              to="/"
              className="flex items-center gap-3 group transition-transform hover:scale-[1.02] flex-shrink-0 w-fit"
            >
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <img
                  src="/favicon/apple-touch-icon.png"
                  alt="Lezzet Yolda Logo"
                  className="w-full h-full object-contain p-1.5"
                />
              </div>
              <h1 className="text-2xl font-extrabold bg-gradient-primary bg-clip-text text-transparent tracking-tight">
                Lezzet Yolda
              </h1>
            </Link>

            {!isCustomerFacingHidden && (
              <div className="hidden lg:flex items-center gap-4 max-w-3xl mx-6 min-w-0 justify-self-center">
                <div className="flex-shrink-0">
                  <CitySelector variant="header" />
                </div>
                <div className="flex-shrink-0">
                  <AddressSelector
                    restaurantId={undefined}
                    onAddressChange={() => {}}
                  />
                </div>

                {/* Premium Search Bar */}
                <div ref={headerSearchContainerRef} className="relative flex-1 group">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-pink-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  <div className="relative">
                    <Icon
                      name="search"
                      size={20}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary transition-colors duration-200 z-10"
                    />
                    <input
                      type="text"
                      value={headerSearchQuery}
                      onChange={(e) => setHeaderSearchQuery(e.target.value)}
                      onFocus={() => {
                        if (headerSearchQuery.length >= 2 && (headerSearchResults.restaurants.length > 0 || headerSearchResults.products.length > 0)) {
                          setShowHeaderSearchResults(true);
                        }
                      }}
                      placeholder="Restoran veya yemek ara..."
                      className="w-full pl-12 pr-5 py-3.5 bg-gray-50/80 border border-gray-200/80 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 focus:bg-white transition-all duration-200 text-sm font-medium placeholder:text-gray-400 shadow-sm hover:shadow-md"
                    />
                    {headerSearchQuery.length > 0 && (
                      <button
                        onClick={() => {
                          setHeaderSearchQuery('');
                          setShowHeaderSearchResults(false);
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      >
                        <Icon name="close" size={16} />
                      </button>
                    )}
                  </div>
                  <SearchResultsDropdown
                    restaurants={headerSearchResults.restaurants}
                    products={headerSearchResults.products}
                    query={headerSearchQuery}
                    isLoading={isHeaderSearching}
                    isVisible={showHeaderSearchResults}
                    onClose={() => setShowHeaderSearchResults(false)}
                    anchorRef={headerSearchContainerRef}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 min-w-0">
              {!isCustomerFacingHidden && (
                <div className="lg:hidden flex-shrink-0 flex items-center gap-2 mr-2">
                  <CitySelector variant="header" />
                  <AddressSelector
                    restaurantId={undefined}
                    onAddressChange={() => {}}
                  />
                </div>
              )}

              {/* Actions - Premium Design */}
              <div className="flex items-center gap-2 flex-shrink-0">
              {isAuthenticated && user?.role === 'RESTAURANT' && (
                <ToptanNavButton />
              )}
              {!isCustomerFacingHidden && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative p-3 hover:bg-primary/5 rounded-2xl transition-all duration-200 group hover:scale-105"
                  aria-label="Sepet"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-pink-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <Icon
                    name="cart"
                    size={24}
                    className="relative text-gray-700 group-hover:text-primary transition-colors duration-200"
                  />
                  {cartItemsCount > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[24px] h-[24px] flex items-center justify-center bg-gradient-to-br from-primary to-pink-600 text-white text-xs font-bold rounded-full shadow-lg border-2 border-white transform group-hover:scale-110 transition-transform">
                      {cartItemsCount > 99 ? '99+' : cartItemsCount}
                    </div>
                  )}
                </button>
              )}

              {/* User Menu - Premium Design */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-gray-50 rounded-2xl transition-all duration-200 group hover:scale-105 border border-transparent hover:border-gray-200"
                    aria-label="Kullanıcı menüsü"
                  >
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/10 to-pink-500/10 flex items-center justify-center border border-primary/20 group-hover:border-primary/40 transition-colors">
                      <Icon name="user" size={18} className="text-primary" />
                    </div>
                    <span className="hidden xl:inline text-sm font-semibold text-gray-800 max-w-[120px] truncate">
                      {userDisplayName}
                    </span>
                    <Icon
                      name="chevron-down"
                      size={16}
                      className={`text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'transform rotate-180' : ''}`}
                    />
                  </button>

                  {/* Premium Dropdown Menu */}
                  {isUserMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[99] bg-black/5"
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 z-[100] overflow-hidden">
                        {user?.role === 'ADMIN' && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-yellow-50 transition-colors duration-200 group border-b border-gray-100"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                              <Icon name="star" size={16} className="text-yellow-600" />
                            </div>
                            <span className="text-gray-800">Admin Paneli</span>
                          </Link>
                        )}
                        {user?.role === 'RESTAURANT' && (
                          <Link
                            to="/restaurant-panel"
                            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-primary/5 transition-colors duration-200 group border-b border-gray-100"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                              <Icon name="store" size={16} className="text-primary" />
                            </div>
                            <span className="text-gray-800">Restoran Paneli</span>
                          </Link>
                        )}
                        {user?.role !== 'RESTAURANT' && user?.role !== 'ADMIN' && (
                          <>
                            <Link
                              to="/orders"
                              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-blue-50 transition-colors duration-200 group"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                <Icon name="list" size={16} className="text-blue-600" />
                              </div>
                              <span className="text-gray-800">Siparişlerim</span>
                            </Link>
                            <Link
                              to="/addresses"
                              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-primary/5 transition-colors duration-200 group"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                <Icon name="location" size={16} className="text-primary" />
                              </div>
                              <span className="text-gray-800">Kayıtlı Adreslerim</span>
                            </Link>
                          </>
                        )}
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-primary/5 transition-colors duration-200 group border-t border-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <Icon name="user" size={16} className="text-primary" />
                          </div>
                          <span className="text-gray-800">Profilim</span>
                        </Link>
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors duration-200 group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                            <Icon name="logout" size={16} className="text-red-600" />
                          </div>
                          <span className="text-red-600">Çıkış Yap</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                >
                  Giriş Yap
                </Button>
              )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default Header;
